import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg, fmtDate, dueClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, FileText, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { insurer: "", policy_number: "", cover_type: "", start_date: "", expiry_date: "", phone: "", email: "", notes: "", vehicle_ids: [] };

export default function InsurancePage() {
  const { canEdit } = useAuth();
  const editable = canEdit("insurance");
  const [policies, setPolicies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/insurance").then((r) => setPolicies(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleVehicle = (id) =>
    setForm((f) => ({ ...f, vehicle_ids: f.vehicle_ids.includes(id) ? f.vehicle_ids.filter((x) => x !== id) : [...f.vehicle_ids, id] }));

  const openAdd = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => { f[k] = k === "vehicle_ids" ? (p[k] || []) : (p[k] || ""); });
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    if (!form.insurer.trim()) return toast.error("Insurer / company name is required");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editId) await api.put(`/insurance/${editId}`, payload);
      else await api.post("/insurance", payload);
      toast.success(editId ? "Policy updated" : "Policy added");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete policy from ${p.insurer}?`)) return;
    await api.delete(`/insurance/${p.id}`);
    toast.success("Policy deleted");
    load();
  };

  const uploadPdf = async (p, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/insurance/${p.id}/pdf`, fd);
      toast.success("Insurance document uploaded");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const viewPdf = async (p) => {
    try {
      const res = await api.get(`/insurance/${p.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" })), "_blank");
    } catch { toast.error("No document uploaded"); }
  };

  const FIELDS = [
    ["Insurer / company *", "insurer", "text", "policy-insurer-input"],
    ["Policy number", "policy_number", "text", "policy-number-input"],
    ["Cover type", "cover_type", "text", "policy-cover-input"],
    ["Start date", "start_date", "date", "policy-start-input"],
    ["Expiry date", "expiry_date", "date", "policy-expiry-input"],
    ["Phone", "phone", "text", "policy-phone-input"],
    ["Email", "email", "text", "policy-email-input"],
    ["Notes", "notes", "text", "policy-notes-input"],
  ];

  return (
    <div className="p-8" data-testid="insurance-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">INSURANCE</h1>
          <p className="text-muted-foreground text-sm mt-1">{policies.length} policies — expiring policies appear in dashboard reminders 1 week ahead</p>
        </div>
        {editable && (
        <Button data-testid="add-policy-button" onClick={openAdd} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
          <Plus size={15} className="mr-1" /> Add policy
        </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-none text-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">{editId ? "EDIT POLICY" : "ADD INSURANCE POLICY"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([label, key, type, tid]) => (
              <div key={key} className={key === "notes" ? "col-span-2" : ""}>
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
                <Input data-testid={tid} type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1.5 rounded-none bg-background border-border h-9" />
              </div>
            ))}
          </div>
          {vehicles.length > 0 && (
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Covered vehicles ({form.vehicle_ids.length} selected)</Label>
              <div className="mt-1.5 border border-border max-h-44 overflow-y-auto grid grid-cols-2" data-testid="policy-vehicles-picker">
                {vehicles.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/60">
                    <input
                      type="checkbox"
                      data-testid={`policy-vehicle-${v.registration}`}
                      checked={form.vehicle_ids.includes(v.id)}
                      onChange={() => toggleVehicle(v.id)}
                      className="accent-[#FF3B30]"
                    />
                    <span className="font-mono font-bold">{v.registration}</span>
                    <span className="text-muted-foreground truncate">{v.make || ""}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="save-policy-button" onClick={save} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">{editId ? "Save changes" : "Save policy"}</Button>
        </DialogContent>
      </Dialog>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="policies-table">
          <thead>
            <tr className="text-left border-b border-border">
              {["Insurer", "Policy no", "Cover", "Vehicles", "Start", "Expiry", "Contact", "Document", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} data-testid={`policy-row-${p.insurer}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{p.insurer}</td>
                <td className="px-4 py-3 font-mono whitespace-nowrap">{p.policy_number || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.cover_type || "—"}</td>
                <td className="px-4 py-3 text-xs" data-testid={`policy-vehicles-${p.insurer}`}>
                  {p.vehicle_regs?.length ? (
                    <div className="max-w-[220px]">
                      <span className="font-bold">{p.vehicle_regs.length} vehicle{p.vehicle_regs.length === 1 ? "" : "s"}</span>
                      <div className="text-muted-foreground font-mono truncate" title={p.vehicle_regs.join(", ")}>
                        {p.vehicle_regs.slice(0, 3).join(", ")}{p.vehicle_regs.length > 3 ? ` +${p.vehicle_regs.length - 3}` : ""}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(p.start_date)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${dueClass(p.expiry_date)}`}>{fmtDate(p.expiry_date)}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {p.phone && <div>{p.phone}</div>}
                  {p.email && <div>{p.email}</div>}
                  {!p.phone && !p.email && "—"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {p.pdf_name && (
                      <button data-testid={`view-policy-pdf-${p.insurer}`} onClick={() => viewPdf(p)} className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-[#32D74B] hover:underline">
                        <FileText size={13} /> View
                      </button>
                    )}
                    {editable && (
                    <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                      <Upload size={13} /> {p.pdf_name ? "Replace" : "Upload"}
                      <input data-testid={`upload-policy-pdf-${p.insurer}`} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadPdf(p, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {editable && (
                  <div className="flex items-center gap-2">
                    <button data-testid={`edit-policy-${p.insurer}`} onClick={() => openEdit(p)} className="text-muted-foreground/70 hover:text-foreground transition-colors duration-150"><Pencil size={14} /></button>
                    <button data-testid={`delete-policy-${p.insurer}`} onClick={() => remove(p)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground/70">No insurance policies yet. Add your fleet policy with number, dates and document.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
